import { Box, Button, Flex, IconLoading, Label, LegacySelect, Text } from "@powerledger/ui-component-lib";
import { Trans, useTranslation } from "react-i18next";

import { Input } from "@/app/components/input";
import { getSelectTranslation } from "@/app/lib/get-translations-for-components";
import { DocumentType } from "@/app/types/generated/graphql";
import { ReactComponent as SampleDoc } from "@/assets/images/sampleDoc.svg";

import { DocUploadViewProps } from "./doc-upload.types";
import { requiredDocAcceptTypeMap } from "./use-doc-upload";

const docTypeOptions = [
  { label: "U.S. government-issued photo ID card", value: DocumentType.IdCard },
  { label: "State issued driver's license", value: DocumentType.License },
  { label: "U.S. government-issued passport", value: DocumentType.Passport },
];

export const DocUploadView = ({
  requiredDocument,
  isUSPerson,
  onFinish,
  setSelectedFile,
  docType,
  setDocType,
  loading,
  selectedFile,
  uploadDocument,
  error,
}: DocUploadViewProps) => {
  const { t } = useTranslation();
  return (
    <>
      <Flex sx={{ fontSize: 1 }}>
        <Box sx={{ flex: 1, pr: 2 }}>
          <Trans>
            <Box
              sx={{
                "& ul": { listStyle: "none", padding: 0 },
                "& ul li": { overflow: "visible", pl: 3, position: "relative" },
                "& ul li:before": { left: 0, position: "absolute" },
              }}
            >
              <Box sx={{ "& ul li:before": { content: "'\\2713'" } }}>
                <Text sx={{ fontWeight: "bold" }}>Acceptable documents</Text>
                <ul>
                  <li>Non-expired State Issued Driver’s License/Identification Card</li>
                  <li>Non-expired US Passport</li>
                  <li>Federal Employment Authorization Card</li>
                  <li>US Visa</li>
                  <li>
                    For Partnerships, General Partnerships, Limited Liability Corporation (LLC) or Corporations: EIN
                    Letter (IRS-issued SS4 confirmation letter)
                  </li>
                  <li>
                    Filed and stamped Articles of Organization or Incorporation, Sales/Use Tax License, Business
                    License, Certificate of Good Standing from any US government entity (accepted on a case-by-case
                    basis)
                  </li>
                </ul>
              </Box>
              <Box sx={{ "& ul li:before": { content: "'\\2717'" } }}>
                <Text sx={{ fontWeight: "bold" }}>Rejectable documents</Text>
                <ul>
                  <li>Military IDs</li>
                  <li>Expired government-issue IDs</li>
                  <li>Non-government issued documents</li>
                </ul>
              </Box>
            </Box>
          </Trans>
        </Box>
        <Box
          sx={{
            flex: 1,
            px: 1,
            "& ul": { pl: 3 },
          }}
        >
          <Trans>
            <Text>
              Please ensure your uploaded file matches the following criteria, to minimize the chances of rejection.
            </Text>
            <ul>
              <li>All four corners of your photographed document must be visible</li>
              <li>Align your document squarely in the photograph (no rotation or tilting)</li>
              <li>Flatten any creases or page lifts</li>
              <li>Photograph your document against a dark background so it contrasts</li>
              <li>Take a high quality image, using the maximum resolution of your camera</li>
              <li>Files must be no larger than 10MB in size</li>
              <li>Don&apos;t use flash or glaring overhead lights</li>
              <li>Take your photos in colour, not B&W</li>
              <li>Don&apos;t photograph expired documents</li>
            </ul>
          </Trans>
        </Box>
        <Box sx={{ flex: 1, ml: 2, pl: 1 }}>
          <SampleDoc />
        </Box>
      </Flex>
      <Box sx={{ width: "67%" }}>
        <Input
          type="file"
          id={`documentUpload-${requiredDocument?.id}`}
          accept={requiredDocument?.type ? requiredDocAcceptTypeMap[requiredDocument.type] : ""}
          sx={{ display: "none" }}
          aria-label="Document Upload"
          onChange={(event) => setSelectedFile(((event.target as HTMLInputElement).files as FileList)[0])}
        />
        {isUSPerson && (
          <LegacySelect
            translation={getSelectTranslation(t)}
            placeholder={t("Please select the type of the uploaded document")}
            options={docTypeOptions}
            isClearable={false}
            value={docTypeOptions.find((t) => t.value === docType)}
            onChange={(option) => setDocType(option?.value)}
            menuPlacement="top"
            sx={{ mt: 3 }}
          />
        )}
        <Flex sx={{ mt: 3, alignItems: "center" }}>
          <Label
            sx={{
              cursor: loading || !docType ? "not-allowed" : "pointer",
              pointerEvents: loading || !docType ? "none" : "auto",
            }}
            htmlFor={`documentUpload-${requiredDocument?.id}`}
          >
            <Input
              placeholder="Select a file to upload..."
              sx={{ pointerEvents: "none" }}
              onChange={() => {}}
              value={selectedFile?.name || ""}
              disabled={loading || !docType}
            />
          </Label>
          <Button
            variant="primary"
            sx={{ ml: 3, flexShrink: 0 }}
            disabled={loading || !selectedFile || !docType}
            onClick={uploadDocument}
            data-testid={"doc-upload--upload"}
          >
            {t("Upload")}
          </Button>
          {loading && <IconLoading sx={{ ml: 2 }} size="small" />}
          <Button
            variant="inverted"
            sx={{ ml: 2, flexShrink: 0 }}
            onClick={onFinish}
            data-testid={"doc-upload--cancel"}
          >
            {t("Cancel")}
          </Button>
        </Flex>
        {error && (
          <Box
            sx={{ color: "negative", fontSize: 0, my: 2, textAlign: "left", mt: 2 }}
            data-testid={"doc-upload--error"}
          >
            {error}
          </Box>
        )}
      </Box>
    </>
  );
};
